import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  activeFiltersToQuerySubmission,
  useQuickSearchHotkey,
  useResourceView,
  type FilterDefinition,
  type QuickSearchProperty,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import {
  eventScopeToCondition,
  type EventQueryScope,
} from "@/shared/domain/events/EventQueryScope";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { eventsTableColumns } from "@/shared/domain/events/EventsTableColumns";
import {
  eventQueryKeys,
  useEventMetadataKeys,
} from "@/shared/domain/events/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  colId,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
} from "@/shared/foundation/primitives/Table";

import { EventDetails } from "./EventDetails";
import styles from "./EventsList.module.css";

const STATIC_EVENT_PICKER_OPTIONS: ColumnPickerOption[] =
  eventsTableColumns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Events };

// Quick find routes a typed value to one field: an `ev_`-shaped value to the
// id, everything else to the name. Categorical filters (tags) live in the
// Filter menu, not here.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  {
    field: "event_id",
    name: "ID",
    // An event id is `ev_` plus non-space characters — anchored so a phrase
    // merely starting that way falls through to the name search.
    pattern: /^ev_\S+$/,
  },
  { field: "name", name: "Name", fallback: true },
];

interface EventsListProps {
  scope: EventQueryScope;
  selectionOptions?: SelectionOption<EventRecord>[];
}

export function EventsList({ scope, selectionOptions }: EventsListProps) {
  const { currentOrganization } = useAuth();
  const { events } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const quickSearchState = useQuickSearchHotkey();
  const { preferences, savePreferences } = useEventTablePreferences();
  const metadataKeysQuery = useEventMetadataKeys(resolvedOrgId);

  // Capture initial columns preferences once at mount
  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<EventRecord>({
    entityType: TargetEntityType.Event,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  // Custom-field columns come before metadata columns (after the static set).
  const columns = React.useMemo<ColDef<EventRecord>[]>(
    () =>
      buildColumnsForPreferences<EventRecord>({
        staticColumns: eventsTableColumns,
        dynamicColumns: [
          ...customFields.customFieldColumns,
          ...(metadataKeysQuery.data ?? []).map<ColDef<EventRecord>>((key) => ({
            colId: `metadata.${key}`,
            headerName: key,
            // Floor so a flex metadata column doesn't collapse on overflow.
            minWidth: 120,
            valueGetter: (p) => p.data?.metadata?.[key],
            valueFormatter: (p) => formatMetadataValue(p.value),
            cellRenderer: renderDashOrValue,
            sortable: false,
          })),
        ],
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [
      customFields.customFieldColumns,
      customFields.effectivePreferenceOrder,
      metadataKeysQuery.data,
    ],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_EVENT_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [customFields.columnPickerOptions, metadataKeysQuery.data],
  );

  const view = useResourceView<EventRecord>({
    getRowId: (row) => row.event_id,
    initialSort: { columnId: "start_time", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "event_id", name: "Event Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      { type: "numeric", field: "duration", name: "Duration (ns)" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: function getEventTags() {
          return events.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
      ...customFields.filterDefinitions,
    ],
    [customFields.filterDefinitions, events, resolvedOrgId],
  );

  const submission = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
  });

  const query = composeQueryBody({
    scopeCondition: eventScopeToCondition(scope),
    filterCondition: submission.filterCondition,
    sort: submission.sort,
    maxResults: submission.maxResults,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Events,
    // Events ship with metadata
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: eventQueryKeys.list(resolvedOrgId, scope, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<EventRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const renderExpandedRow = React.useCallback(
    (row: EventRecord) => <EventDetails event={row} readonly={false} />,
    [],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by ID or name…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find events"
          />
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<EventRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <DataTable<EventRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No events"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </ResourceView.Root>
    </>
  );
}
